import type { ReadonlySignal } from '@preact/signals'
import { signal } from '@preact/signals'
import { createContext } from 'preact'
import { useContext } from 'preact/hooks'
import type { InventoryTarget } from '#contract'
import { assert } from '#shared'

/** A point in client pixels. */
interface Point {
  readonly x: number
  readonly y: number
}

/** The slot under the pointer, or the one a gamepad focused: what the tooltip is about. */
interface Hover {
  readonly target: InventoryTarget
  readonly element: HTMLElement
}

/**
 * The inventory screen's own pointer state, shared by its parts as signals so a moving
 * pointer re-renders nothing. Everything about the items comes from the view.
 */
export interface ScreenUi {
  /** Where the pointer (or finger) last was; the held stack follows it. */
  readonly pointer: ReadonlySignal<Point | null>
  readonly hover: ReadonlySignal<Hover | null>
  /** Touch's "Quick move" toggle: taps move across instead of taking. */
  readonly quick: ReadonlySignal<boolean>
  movePointer(point: Point): void
  /** Hovers `hover`, or clears it (`null`). */
  setHover(hover: Hover | null): void
  /** Clears the hover only if it's still on `element`. */
  leave(element: HTMLElement): void
  toggleQuick(): void
}

/** One screen's pointer state; made once per open screen. */
export function createScreenUi(): ScreenUi {
  const pointer = signal<Point | null>(null)
  const hover = signal<Hover | null>(null)
  const quick = signal(false)
  return {
    pointer,
    hover,
    quick,
    movePointer(point) {
      pointer.value = point
    },
    setHover(next) {
      hover.value = next
    },
    leave(element) {
      if (hover.value?.element === element) hover.value = null
    },
    toggleQuick() {
      quick.value = !quick.value
    },
  }
}

export const ScreenUiContext = createContext<ScreenUi | null>(null)

export function useScreenUi(): ScreenUi {
  const ui = useContext(ScreenUiContext)
  assert(ui, 'Inventory parts need the <InventoryScreen> above them')
  return ui
}

/** A slot's `data-target`: `player:3`, `other:0` or `bin`. */
export function targetKey(target: InventoryTarget): string {
  return target === 'bin' ? 'bin' : `${target.side}:${String(target.slot)}`
}

/** The target an element (or its nearest slot) stands for, from its `data-target`. */
export function targetOf(element: Element | null): InventoryTarget | null {
  const key = element?.closest<HTMLElement>('[data-target]')?.dataset.target
  if (key === 'bin') return 'bin'
  const [side, slot] = key?.split(':') ?? []
  if ((side !== 'player' && side !== 'other') || slot === undefined) return null
  return { side, slot: Number(slot) }
}

export function sameTarget(a: InventoryTarget | null, b: InventoryTarget | null): boolean {
  return a !== null && b !== null && targetKey(a) === targetKey(b)
}
